import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import type { AppMetadataClaims, AuthStatus, ClinicMembershipClaim } from '@/types/auth';
import {
  permissionMapHas,
  type PermissionAction,
  type PermissionMap,
  type PermissionResource,
} from '@/lib/permissions';

/**
 * Decodes the payload segment of a JWT access token.
 * Used to read hook-baked claims that are not mirrored on auth.users.app_metadata.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Reads JWT claims baked by `public.custom_access_token_hook` (FND-4).
 *
 * IMPORTANT: the hook writes into the **access token JWT**, not into
 * auth.users.raw_app_meta_data. For new users, session.user.app_metadata only
 * contains provider fields — clinic_memberships must be read from the JWT.
 */
function readClaims(session: Session | null): AppMetadataClaims {
  const fromUser = (session?.user?.app_metadata ?? {}) as Partial<AppMetadataClaims>;

  let raw = fromUser;
  if (session?.access_token) {
    const payload = decodeJwtPayload(session.access_token);
    const jwtMeta = payload?.app_metadata;
    if (jwtMeta && typeof jwtMeta === 'object' && !Array.isArray(jwtMeta)) {
      const fromJwt = jwtMeta as Partial<AppMetadataClaims>;
      const jwtHasBakedAt =
        typeof fromJwt.claims_baked_at === 'number' && fromJwt.claims_baked_at > 0;

      // Prefer hook-baked JWT claims whenever the access token hook has run.
      if (jwtHasBakedAt) {
        raw = { ...fromUser, ...fromJwt };
      }
    }
  }

  return {
    primary_role: raw.primary_role ?? 'clinic_user',
    is_platform_admin: Boolean(raw.is_platform_admin),
    is_main_clinic_admin: Boolean(raw.is_main_clinic_admin),
    primary_clinic_id: raw.primary_clinic_id ?? null,
    clinic_memberships: Array.isArray(raw.clinic_memberships) ? raw.clinic_memberships : [],
    claims_invalidated_at:
      typeof raw.claims_invalidated_at === 'number' ? raw.claims_invalidated_at : 0,
    claims_baked_at: typeof raw.claims_baked_at === 'number' ? raw.claims_baked_at : 0,
  };
}

interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  session: Session | null;
  claims: AppMetadataClaims;
  memberships: ClinicMembershipClaim[];
  primaryClinicId: string | null;
  isPlatformAdmin: boolean;
  isMainClinicAdmin: boolean;
  isClinicAdmin: (clinicId: string) => boolean;
  isClinicMember: (clinicId: string) => boolean;
  hasPermission: (
    clinicId: string | null | undefined,
    resource: PermissionResource,
    action: PermissionAction,
  ) => boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string, redirectTo?: string) => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthStatus>('initializing');
  const [dbMemberships, setDbMemberships] = useState<ClinicMembershipClaim[]>([]);
  const mountedRef = useRef(true);
  const intentionalSignOutRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    void (async () => {
      const { data, error } = await supabase.auth.getSession();
      if (!mountedRef.current) return;
      if (error) {
        console.error('auth: getSession failed', error);
      }
      setSession(data.session);
      setStatus(data.session ? 'authenticated' : 'unauthenticated');
    })();

    const { data: subscription } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mountedRef.current) return;

      // Detect surprise logouts (token refresh failure, not user-initiated).
      // Store a flag so the sign-in page can show a helpful "session expired" message.
      if (event === 'SIGNED_OUT' && !intentionalSignOutRef.current) {
        try {
          sessionStorage.setItem('clinio_session_expired', '1');
        } catch {
          // sessionStorage not available
        }
      }
      intentionalSignOutRef.current = false;

      setSession(nextSession);
      setStatus(nextSession ? 'authenticated' : 'unauthenticated');
    });

    return () => {
      subscription.subscription.unsubscribe();
    };
  }, []);

  const claims = useMemo(() => readClaims(session), [session]);

  const jwtMemberships = claims.clinic_memberships;

  // When JWT claims are empty, load memberships directly from DB (RLS allows
  // users to read their own clinic_memberships rows).
  useEffect(() => {
    if (!session?.user || jwtMemberships.length > 0) {
      setDbMemberships([]);
      return;
    }

    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from('clinic_memberships')
        .select('clinic_id, role, created_at')
        .eq('status', 'active')
        .order('created_at', { ascending: true });

      if (cancelled) return;
      if (error) {
        console.error('auth: bootstrap memberships failed', error);
        return;
      }

      setDbMemberships(
        (data ?? []).map((row) => ({
          clinic_id: row.clinic_id,
          role: row.role,
          is_accessible: true,
          is_active: true,
        })),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id, jwtMemberships.length]);

  const effectiveMemberships =
    jwtMemberships.length > 0 ? jwtMemberships : dbMemberships;

  const effectivePrimaryClinicId =
    claims.primary_clinic_id ?? effectiveMemberships[0]?.clinic_id ?? null;

  const effectiveIsMainClinicAdmin =
    claims.is_main_clinic_admin ||
    effectiveMemberships.some((m) => m.role === 'main_clinic_admin');

// Stale-claim detection: refresh when JWT claims are missing
  // or older than the server. Common after signup/invite flows where membership
  // is created in the same request as the first sign-in token.
  const claimsRefreshAttemptedRef = useRef(false);
  useEffect(() => {
    // Allow a fresh refresh attempt when the signed-in user changes.
    claimsRefreshAttemptedRef.current = false;
  }, [session?.user?.id]);

  useEffect(() => {
    if (!session || claimsRefreshAttemptedRef.current) return;

    const needsRefresh =
      jwtMemberships.length === 0 &&
      dbMemberships.length === 0 &&
      (claims.claims_baked_at === 0 ||
        claims.claims_invalidated_at > claims.claims_baked_at);

    if (!needsRefresh) return;

    claimsRefreshAttemptedRef.current = true;
    void supabase.auth.refreshSession().catch((err) => {
      console.error('auth: refreshSession failed', err);
      claimsRefreshAttemptedRef.current = false;
    });
  }, [
    claims.claims_baked_at,
    claims.claims_invalidated_at,
    claims.clinic_memberships.length,
    claims.is_platform_admin,
    dbMemberships.length,
    jwtMemberships.length,
    session,
  ]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    // Note: the custom_access_token_hook (FND-4) bakes claims at sign-in time.
    // If claims are missing the stale-claim detection below handles a single
    // safe refresh. We no longer do an unconditional second refresh here because
    // that created a token-rotation race condition for new clinic users.
  }, []);

  // Note: there is no public signUp on this hook by design. Per MOD-1, account
  // creation goes through the `accept-invitation` Edge Function (token + chosen
  // password) — not via supabase.auth.signUp from the browser.

  const signOut = useCallback(async () => {
    intentionalSignOutRef.current = true;
    const { error } = await supabase.auth.signOut();
    if (error) {
      intentionalSignOutRef.current = false;
      throw error;
    }
  }, []);

  const sendPasswordReset = useCallback(async (email: string, redirectTo?: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });
    if (error) throw error;
  }, []);

  const refreshSession = useCallback(async () => {
    const { error } = await supabase.auth.refreshSession();
    if (error) throw error;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user: session?.user ?? null,
      session,
      claims,
      memberships: effectiveMemberships,
      primaryClinicId: effectivePrimaryClinicId,
      isPlatformAdmin: claims.is_platform_admin,
      isMainClinicAdmin: effectiveIsMainClinicAdmin,
      isClinicMember: (clinicId: string) =>
        effectiveMemberships.some((m) => m.clinic_id === clinicId),
      isClinicAdmin: (clinicId: string) =>
        effectiveMemberships.some(
          (m) =>
            m.clinic_id === clinicId &&
            (m.role === 'clinic_admin' || m.role === 'main_clinic_admin'),
        ),
      hasPermission: (clinicId, resource, action) => {
        if (!clinicId) return false;
        if (claims.is_platform_admin) return true;
        const membership = effectiveMemberships.find((m) => m.clinic_id === clinicId);
        if (!membership) return false;
        if (membership.role === 'clinic_admin' || membership.role === 'main_clinic_admin') {
          return true;
        }
        // null = implicit all (admin-style); undefined = legacy JWT before RBAC bake
        if (membership.permissions === null) return true;
        if (membership.permissions === undefined) {
          if (
            resource === 'settings_users' ||
            resource === 'settings_roles' ||
            resource === 'settings_billing' ||
            resource === 'multi_clinic' ||
            resource === 'audit_logs'
          ) {
            return false;
          }
          return action !== 'delete';
        }
        return permissionMapHas(membership.permissions as PermissionMap, resource, action);
      },
      signIn,
      signOut,
      sendPasswordReset,
      refreshSession,
    }),
    [claims, session, signIn, signOut, sendPasswordReset, refreshSession, status, effectiveMemberships, effectivePrimaryClinicId, effectiveIsMainClinicAdmin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
